import { useEffect, useMemo } from 'react';
import * as Select from '@radix-ui/react-select';
import clsx from 'clsx';
import { Check, ChevronDown, Facebook, AlertTriangle } from 'lucide-react';
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner/LoadingSpinner';
import { useMetaPages } from '../../../../api/campaigns-queries';
import styles from './MetaPagePicker.module.scss';

interface MetaPagePickerProps {
  /** UUID of the connected Meta ad account chosen by the user. */
  adAccountId: string;
  /** Currently selected Page id (form state). */
  value?: string;
  /** Selected Page display name; mostly for the badge in the review step. */
  selectedName?: string;
  onChange: (pageId: string, pageName: string) => void;
  /** Validation error from the form. */
  error?: string;
}

/**
 * Lets the organizer pick which Facebook Page the ad will be published as.
 * Meta refuses to create any Ad without `object_story_spec.page_id`, so the
 * wizard can't move to launch without one. Auto-selects when the user has a
 * single Page (most common), shows a dropdown when there are multiple, and
 * prompts the user to connect a Page when none are available.
 */
export function MetaPagePicker({
  adAccountId,
  value,
  // selectedName is accepted for caller convenience but the dropdown now
  // relies on Radix's cloned ItemText instead of an injected label.
  selectedName: _selectedName,
  onChange,
  error,
}: MetaPagePickerProps) {
  const { data, isLoading, isError, error: queryError } = useMetaPages(adAccountId);
  const items = useMemo(() => data?.pages ?? [], [data?.pages]);

  // When there's exactly one Page, pick it for the user. They can still see
  // and confirm it in the review step but don't have to interact here.
  useEffect(() => {
    if (items.length !== 1 || value) return;
    const only = items[0];
    if (!only) return;
    onChange(only.id, only.name);
  }, [items, value, onChange]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <LoadingSpinner />
        <span className={styles.helper}>Loading your Facebook Pages…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={clsx(styles.container, styles.errorState)}>
        <AlertTriangle size={16} />
        <span>
          Couldn&apos;t load Facebook Pages. {queryError instanceof Error ? queryError.message : ''}
        </span>
      </div>
    );
  }

  if (items.length === 0) {
    // Backend tells us whether the token is missing the Pages scope or the
    // user simply doesn't admin any Pages — render the matching CTA.
    if (data?.reason === 'MISSING_SCOPE') {
      return (
        <div className={clsx(styles.container, styles.emptyState)}>
          <AlertTriangle size={16} />
          <div>
            <strong>Reconnect Meta to enable Page access.</strong>
            <p>
              Your Meta connection is missing the permission to list Pages. Reconnect and approve
              the Pages scope to continue.{' '}
              {data.reconnectUrl && (
                <a href={data.reconnectUrl} rel="noopener noreferrer">
                  Reconnect Meta →
                </a>
              )}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={clsx(styles.container, styles.emptyState)}>
        <Facebook size={16} />
        <div>
          <strong>No Facebook Pages found on this account.</strong>
          <p>
            Create a Page on Facebook (or connect one in Business Manager), then come back to this
            step.{' '}
            <a
              href={data?.createUrl ?? 'https://www.facebook.com/pages/create/'}
              target="_blank"
              rel="noopener noreferrer"
            >
              Create a Page →
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Single Page → render as read-only confirmation, not a dropdown.
  const only = items.length === 1 ? items[0] : undefined;
  if (only) {
    return (
      <div className={clsx(styles.container, styles.autoSelected)}>
        <Facebook size={16} />
        <div>
          <span className={styles.helperLabel}>Publishing as</span>
          <strong>{only.name}</strong>
          {only.category && <span className={styles.category}>{only.category}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <label className={styles.label}>Facebook Page</label>
      <Select.Root
        value={value}
        onValueChange={(id) => {
          const picked = items.find((p) => p.id === id);
          if (picked) onChange(picked.id, picked.name);
        }}
      >
        <Select.Trigger
          className={clsx(styles.trigger, error && styles.triggerError)}
          aria-invalid={!!error}
        >
          {/*
            Letting Radix render the cloned ItemText from the selected option
            instead of injecting `selectedName` ourselves. Passing children here
            (even conditionally) was conflicting with the clone after selection
            and tripping React 19's removeChild reconciliation guard.
          */}
          <Select.Value placeholder="Pick the Page that publishes the ad…" />
          <Select.Icon className={styles.icon}>
            <ChevronDown size={16} />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content className={styles.content} position="popper" sideOffset={4}>
            <Select.Viewport>
              {items.map((page) => (
                <Select.Item key={page.id} value={page.id} className={styles.item}>
                  {/*
                    Radix clones Select.ItemText into Select.Value when this item
                    becomes selected. Keep the cloned subtree text-only — anything
                    structural here breaks React 19 reconciliation when the
                    selection flips. Decorative content (category, indicator)
                    stays as siblings.
                  */}
                  <Select.ItemText>{page.name}</Select.ItemText>
                  {page.category && <span className={styles.itemCategory}>{page.category}</span>}
                  <Select.ItemIndicator className={styles.itemIndicator}>
                    <Check size={14} />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      {error && (
        <span className={styles.errorMsg} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
